import { useState } from 'react';

import { sanitize } from 'lib/helpers/sanitize';
import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';

import {
  COLORS,
  VARIANTS,
  getBackgroundClass,
  getBorderClass,
  getIconColor,
  getIconShade,
  getShade
} from './lib/helpers';

/**
 * @param {import('./lib/propTypes').BannerProps} props
 */

const Banner = ({
  icon = 'info-filled',
  color = 'neutral',
  variant = 'outlined',
  title,
  message,
  button,
  isWidthFull = false,
  handleClose,
  className: wrapperClassName = ''
}) => {
  if (!COLORS.includes(color)) color = 'primary';
  if (!VARIANTS.includes(variant)) variant = 'lighter';

  const [buttonLoading, setButtonLoading] = useState(false);

  const handleButtonClick = async () => {
    if (!button?.onClick) return;

    try {
      setButtonLoading(true);
      await button.onClick();
      if (button.closeOnClick) {
        handleClose?.();
      }
    } catch (error) {
      console.error('Button action error:', error);
    } finally {
      setButtonLoading(false);
    }
  };

  const buttonProps = button
    ? (() => {
        const { onClick: _onClick, ...rest } = button;
        return rest;
      })()
    : null;

  return (
    <div
      className={cn(
        'flex h-fit min-h-[48px] w-full items-center gap-3 overflow-hidden rounded-lg px-4 py-2',
        isWidthFull ? 'w-full' : 'max-w-full',
        getBackgroundClass(color, variant),
        getBorderClass(color, variant),
        getShade(variant),
        wrapperClassName
      )}>
      <div className="flex grow flex-wrap items-center justify-center gap-2">
        {icon && (
          <Icon
            icon={icon}
            color={getIconColor(color, variant)}
            shade={getIconShade(color, variant)}
            size={20}
            className="mr-1"
          />
        )}
        {title && (
          <>
            <span
              className={cn(
                'text-[14px] font-500 first-letter:uppercase',
                variant === 'filled' ? 'text-white' : 'text-neutral-900'
              )}>
              {title}
            </span>
            {message && (
              <>
                <span
                  className={cn(
                    'text-[12px]',
                    variant === 'filled' ? 'text-white' : 'text-neutral-500'
                  )}>
                  •
                </span>
                <span
                  className={cn(
                    'text-[14px] font-400 first-letter:uppercase',
                    variant === 'filled' ? 'text-white' : 'text-neutral-800'
                  )}>
                  {typeof message === 'string' && message?.includes?.('<') ? (
                    <span dangerouslySetInnerHTML={sanitize(message)} />
                  ) : (
                    message
                  )}
                </span>
              </>
            )}
          </>
        )}
        {!title && message && (
          <span
            className={cn(
              'text-[14px] font-400 first-letter:uppercase',
              variant === 'filled' ? 'text-white' : 'text-neutral-800'
            )}>
            {typeof message === 'string' && message?.includes?.('<') ? (
              <span dangerouslySetInnerHTML={sanitize(message)} />
            ) : (
              message
            )}
          </span>
        )}
        {button && (
          <Button
            size="small"
            variant="link"
            className="ml-1"
            color={variant === 'filled' ? 'white' : 'neutral'}
            textClassName="underline"
            shade={900}
            text={button.text}
            data-qa={button['data-qa']}
            isLoading={buttonLoading || button.isLoading}
            isDisabled={button.isDisabled || buttonLoading}
            {...buttonProps}
            onClick={handleButtonClick}
          />
        )}
      </div>
      {handleClose && (
        <button
          className="cursor-pointer p-0 hover:opacity-80"
          onClick={handleClose}
          aria-label="Close banner">
          <Icon
            icon="close-linear"
            size={20}
            color={variant === 'filled' ? 'white' : 'neutral'}
            shade={700}
          />
        </button>
      )}
    </div>
  );
};

export default Banner;
